import {
  MAX_OF_DISPLAY_FORECAST_IN_DIFF_LAST_MONTH_PAGE,
  MAX_OF_DISPLAY_FORECAST_IN_RECEIVE_FORECAST_PAGE,
} from '@/coreDomainsModule/constants';
import { IYearMonth } from '@/coreDomainsModule/date';
import { CommonTableHeaderKey } from '@/coreDomainsModule/table';
import { Order } from '@/coreDomainsModule/entities/order'; // Orderをインポート

/**
 * テーブルのヘッダーの定義
 * key: ヘッダーのキー
 * isVariableWidth: 可変幅かどうか
 * isSortable: ソート可能かどうか
 */
export type TableContentHeader = BaseTableContentHeader & Partial<ForecastContentHeader>;

/**
 * テーブルのヘッダーの基本構成の定義
 * key: ヘッダーのキー
 * isVariableWidth: 可変幅かどうか
 * isSortable: ソート可能かどうか
 */
export type BaseTableContentHeader = {
  contentProperties: TableContentProperty[];
  options: TableContentHeaderOption;
};

/**
 * 内示を載せるヘッダーの定義
 */
export type ForecastContentHeader = {
  /**
   * 連続で表示する内示の開始年月
   */
  forecastStartYearMonth: IYearMonth;
};

/**
 * テーブルのヘッダーのオプション
 * stableColumnCount: 固定列数(先頭から数えて何列目まで固定するか)
 * resizableHeaderColumn: ヘッダーの列幅を変更可能かどうか
 */
export type TableContentHeaderOption = {
  stableColumnCount: number;
  resizableHeaderColumn: boolean;
};

/**
 * ソートオプション
 * isSortable: ソート可能かどうか
 * isAsc: 昇順かどうか
 * sortKey: ソートキーに関する情報
 */
export type SortOption = {
  isSortable: boolean;
  isAsc: boolean;
};

/**
 * ソートキーの型
 * string: 文字列
 * number: 数値
 * IDate: 日付
 */
export type KeyType = 'string' | 'number' | 'IDate';

/**
 * プロパティに関する情報
 * paths: プロパティのパス(item.recommend.crossDateの場合は['recommend', 'crossDate'])
 * propertyType: プロパティの型
 */
export type PropertyPath = {
  paths: string[];
  propertyType: KeyType;
};

/**
 * テーブルのヘッダーの定義
 * key: ヘッダーのキー
 * isVariableWidth: 可変幅かどうか
 * isNarrowable: 絞り込み可能かどうか
 * isEditable: 編集可能かどうか
 * sortOption: ソートオプション
 * propertyPath: プロパティに関する情報
 * headerWidth: ヘッダーの幅
 */
export type TableContentProperty = {
  key: TableContentHeaderKey;
  isVariableWidth: boolean;
  isNarrowable: boolean;
  isEditable: boolean;
  sortOption: SortOption;
  propertyPath?: PropertyPath;
  headerWidth?: number;
  displayLabel?: string;
};

/**
 * テーブルのヘッダーのキー
 * CommonTableHeaderKey: 共通のヘッダーのキー
 * OrderHeaderKey: 発注のヘッダーのキー
 */
export type TableContentHeaderKey =
  | CommonTableHeaderKey
  | UniqueHeaderKey
  | OrderHeaderKey
  | ForecastForReceiptHeaderKey
  | ForecastAccuracyHeaderKey
  | UnmanagedBusinessHeaderKey
  | BusinessesForSupplierForecastHeaderKey
  | TrashBinHeaderKey
  | EditBusinessHeaderKey;

/**
 * 特殊なヘッダーのキー
 * IBusinessの中身に関係ない & 汎用的に使用することが想定されるものをまとめる想定
 * Space8px: 8pxのスペース
 */
export type UniqueHeaderKey = 'Space8px';

/**
 * テーブルのヘッダーのキーがUniqueHeaderKeyかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isUniqueHeaderKey = (key: string): key is UniqueHeaderKey => {
  return key === 'Space8px';
};

/**
 * OrderNumber: 発注番号
 * OrderDesiredTimeOfArrival: 希望納期
 * OrderEstimateTimeOfArrival: 納入日
 * OrderAmount: 推奨数量
 * OrderRecommendStatus: 推奨ステータス
 * OrderMemo: メモ
 * OrderAlert: アラート
 */
export type OrderHeaderKey =
  | 'OrderGroupName'
  | 'OrderInvoiceNumber'
  | 'OrderDestinationName'
  | 'OrderNumber'
  | 'OrderDesiredTimeOfArrival'
  | 'OrderEstimateTimeOfArrival'
  | 'OrderAmount'
  | 'OrderRecommendStatus'
  | 'OrderMemo'
  | 'OrderAlert'
  | 'OrderDate'
  | 'OrderOutputDate'
  | 'OrderLastUpdatedBy'
  | 'OrderEditButton'
  | 'OrderDeleteButton'
  | 'ReminderMail'
  | 'OrderBasis'
  | 'OrderIssuedNumber';

/**
 * UnmanagedBusinessのユニークなプロパティのキー
 */
export type UnmanagedBusinessHeaderKey =
  | 'arrivalAmount'
  | 'businessNumberNamedByUser'
  | 'customerAnnounce-1'
  | 'customerAnnounce-2'
  | 'customerAnnounce-3'
  | 'deliveryDestinationNumber'
  | 'deliveryDestinationName'
  | 'fileName'
  | 'inputDate'
  | 'inventory'
  | 'saleQuantity'
  | 'stdInventoryIndex'
  | 'registerBusiness'
  | 'UnmanagedBusinessWarehouseNumber'
  | 'UnmanagedBusinessSupplierNumber'
  | 'UnmanagedBusinessCustomerNumber'
  | 'UnmanagedBusinessSalesDestinationNumber';

/**
 * MoveWareHouseのユニークなプロパティのキー
 */
export type MoveWareHouseHeaderKey = 'differenceOccurrenceMonthYear' | 'differenceAmount';

/**
 * テーブルのヘッダーのキーがOrderHeaderKeyかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isOrderHeaderKey = (key: string): key is OrderHeaderKey => {
  return [
    'OrderGroupName',
    'OrderInvoiceNumber',
    'OrderDestinationName',
    'OrderNumber',
    'OrderDesiredTimeOfArrival',
    'OrderEstimateTimeOfArrival',
    'OrderAmount',
    'OrderRecommendStatus',
    'OrderMemo',
    'OrderAlert',
    'OrderDate',
    'OrderOutputDate',
    'OrderLastUpdatedBy',
    'OrderEditButton',
    'OrderDeleteButton',
    'ReminderMail',
    'OrderBasis',
    'OrderIssuedNumber',
  ].includes(key);
};

/**
 * 受領内示で表示する客先内示
 */
const customerAnnouncesForReceipt = [
  ...new Array(MAX_OF_DISPLAY_FORECAST_IN_RECEIVE_FORECAST_PAGE).fill('customerAnnounceForReceipt').map((x, i) => x + '-' + (i + 1)),
];
/**
 * 受領内示で表示する客先内示のキー
 */
export type CustomerAnnounceForReceiptHeaderKey = (typeof customerAnnouncesForReceipt)[number];

/**
 * 受領内示で表示する客先内示のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isCustomerAnnounceForReceiptKey = (key: string): key is CustomerAnnounceForReceiptHeaderKey => {
  return customerAnnouncesForReceipt.includes(key);
};

/**
 * 前月差異ページで表示する客先内示
 */
const customerForecastWithDiffLastMonth = [
  ...new Array(MAX_OF_DISPLAY_FORECAST_IN_DIFF_LAST_MONTH_PAGE).fill('customerForecastWithDiffLastMonth').flatMap((x, i) => {
    const base = x + '-' + (i + 1);
    // これをさらに3つに分割する
    return [base + '-1', base + '-2', base + '-3'];
  }),
];

/**
 * 前月差異ページで表示する客先内示のキー
 */
export type CustomerForecastWithDiffLastMonthHeaderKey = (typeof customerForecastWithDiffLastMonth)[number];

/**
 * 前月差異ページで表示する客先内示のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isCustomerForecastWithDiffLastMonthKey = (key: string): key is CustomerForecastWithDiffLastMonthHeaderKey => {
  return customerForecastWithDiffLastMonth.includes(key);
};

/**
 * 客先内示のキー
 */
export type ForecastForReceiptHeaderKey =
  | 'ForecastForReceiptStaff'
  | 'ForecastForReceiptRecoverStatus'
  | 'ForecastForReceiptRecoverRate'
  | 'ForecastForReceiptLatestUpdateAt'
  | 'ForecastForReceiptEditOption'
  | 'ForecastForReceiptKeyword'
  | 'ForecastForReceiptStaffFull'
  | 'ForecastForReceiptName'
  | 'ForecastForReceiptResponsibleUser'
  | 'ForecastForReceiptReceiveDate'
  | 'SupplierForecastCreateStatus'
  | 'customerForecastUpdateAt'
  | 'timelineButton'
  | 'ForecastForReceiptRowMenu'
  | 'removeFromForecastButton'
  | CustomerAnnounceForReceiptHeaderKey
  | CustomerForecastWithDiffLastMonthHeaderKey;

/**
 *  客先内示のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isForecastForReceiptKey = (key: string): key is ForecastForReceiptHeaderKey => {
  return [
    'ForecastForReceiptStaff',
    'ForecastForReceiptRecoverStatus',
    'ForecastForReceiptRecoverRate',
    'ForecastForReceiptLatestUpdateAt',
    'ForecastForReceiptEditOption',
    'ForecastForReceiptKeyword',
    'ForecastForReceiptStaffFull',
    'ForecastForReceiptName',
    'ForecastForReceiptResponsibleUser',
    'ForecastForReceiptReceiveDate',
    'SupplierForecastCreateStatus',
    'customerForecastUpdateAt',
    'timelineButton',
    'ForecastForReceiptRowMenu',
    'removeFromForecastButton',
    ...customerAnnouncesForReceipt,
    ...customerForecastWithDiffLastMonth,
  ].includes(key);
};

/**
 * 内示正答率ページで表示する客先内示
 */
const customerAnnouncesForAccuracy = [
  ...new Array(MAX_OF_DISPLAY_FORECAST_IN_RECEIVE_FORECAST_PAGE).fill('customerAnnounceForAccuracy').map((x, i) => x + '-' + (i + 1)),
];
/**
 * 内示正答率ページで表示する客先内示のキー
 */
export type CustomerAnnounceForAccuracyHeaderKey = (typeof customerAnnouncesForAccuracy)[number];

/**
 * 内示正答率ページで表示する客先内示のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isCustomerAnnounceForAccuracyKey = (key: string): key is CustomerAnnounceForAccuracyHeaderKey => {
  return customerAnnouncesForAccuracy.includes(key);
};

/**
 * 内示正答率のキー
 */
export type ForecastAccuracyHeaderKey =
  | 'ForecastAccuracyName'
  | 'ForecastAccuracyResponsibleUser'
  | 'ForecastAccuracyPreviousMonthSalesResult'
  | 'ForecastAccuracyPreviousMonthForecast'
  | 'ForecastAccuracyPreviousMonthAccuracyRate'
  | 'ForecastAccuracyMenu'
  | CustomerAnnounceForAccuracyHeaderKey;

/**
 * 内示正答率のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isForecastAccuracyKey = (key: string): key is ForecastAccuracyHeaderKey => {
  return [
    'ForecastAccuracyName',
    'ForecastAccuracyResponsibleUser',
    'ForecastAccuracyPreviousMonthSalesResult',
    'ForecastAccuracyPreviousMonthForecast',
    'ForecastAccuracyPreviousMonthAccuracyRate',
    'ForecastAccuracyMenu',
    ...customerAnnouncesForAccuracy,
  ].includes(key);
};
/**
 * テーブルのヘッダーのキーがUnmanagedBusinessHeaderKeyかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isUnmanagedBusinessHeaderKey = (key: string): key is UnmanagedBusinessHeaderKey => {
  return [
    'arrivalAmount',
    'businessNumberNamedByUser',
    'customerAnnounce-1',
    'customerAnnounce-2',
    'customerAnnounce-3',
    'deliveryDestinationNumber',
    'deliveryDestinationName',
    'fileName',
    'inputDate',
    'inventory',
    'saleQuantity',
    'stdInventoryIndex',
    'registerBusiness',
    'UnmanagedBusinessWarehouseNumber',
    'UnmanagedBusinessSupplierNumber',
    'UnmanagedBusinessCustomerNumber',
    'UnmanagedBusinessSalesDestinationNumber',
  ].includes(key);
};

/**
 * テーブルのヘッダーのキーがMoveWareHouseHeaderKeyかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isMoveWareHouseHeaderKey = (key: string): key is MoveWareHouseHeaderKey => {
  return ['differenceOccurrenceMonthYear', 'differenceAmount'].includes(key);
};

/**
 * 仕入先内示
 */
const supplierAnnounces = [
  ...new Array(MAX_OF_DISPLAY_FORECAST_IN_RECEIVE_FORECAST_PAGE).fill('supplierAnnounce').map((x, i) => x + '-' + (i + 1)),
];
/**
 * 仕入先内示のキー
 */
export type SupplierAnnounceHeaderKey = (typeof supplierAnnounces)[number];

/**
 * 仕入先内示のキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isSupplierAnnounceKey = (key: string): key is SupplierAnnounceHeaderKey => {
  return supplierAnnounces.includes(key);
};

/**
 * 仕入先内示の全取引テーブルのキー
 */
export type BusinessesForSupplierForecastHeaderKey = 'supplierForecastStaff' | 'supplierForecastUpdateAt' | SupplierAnnounceHeaderKey;

/**
 * 仕入先内示の全取引テーブルのキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isBusinessesForSupplierForecastHeaderKey = (key: string): key is BusinessesForSupplierForecastHeaderKey => {
  return ['supplierForecastStaff', 'supplierForecastUpdateAt', ...supplierAnnounces].includes(key);
};

/**
 * テーブルヘッダーのフォントサイズ
 */
export type TableHeaderFontSize = 'fontSizeSS' | 'fontSizeS' | 'fontSizeM' | 'fontSizeL';

/**
 * 発注管理（OrderControl）のプロパティのキー
 */
export type OrderControlHeaderKey =
  | 'OrderControlChoice'
  | 'OrderConfirmation'
  | 'OrderControlOrderStatus'
  | 'OrderControlOrderDate'
  | 'OrderControlLastCheckedAt'
  | 'OrderControlOrderAmount'
  | 'OrderControlDeliveryDate'
  | 'OrderControlWarehouseNumber'
  | 'OrderControlWarehouse'
  | 'OrderControlSupplierNumber'
  | 'OrderControlSupplierName'
  | 'OrderControlBusinessNumber'
  | 'OrderControlBusinessName'
  | 'OrderControlPackage'
  | 'OrderControlUnit'
  | 'OrderControlManufacturer'
  | 'OrderControlCustomerName'
  | 'OrderControlSalesDestinationName'
  | 'OrderControlAggregateBusinessResponsibleUsers'
  | 'OrderControlLeadTime'
  | 'OrderControlStdInventoryIndex';

/**
 * テーブルのヘッダーのキーがOrderControlのヘッダーキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isOrderControlHeaderKey = (key: string): key is OrderControlHeaderKey => {
  return [
    'OrderControlChoice',
    'OrderConfirmation',
    'OrderControlOrderStatus',
    'OrderControlOrderDate',
    'OrderControlLastCheckedAt',
    'OrderControlOrderAmount',
    'OrderControlDeliveryDate',
    'OrderControlWarehouseNumber',
    'OrderControlWarehouse',
    'OrderControlSupplierNumber',
    'OrderControlSupplierName',
    'OrderControlBusinessNumber',
    'OrderControlBusinessName',
    'OrderControlPackage',
    'OrderControlUnit',
    'OrderControlManufacturer',
    'OrderControlCustomerName',
    'OrderControlSalesDestinationName',
    'OrderControlAggregateBusinessResponsibleUsers',
    'OrderControlLeadTime',
    'OrderControlStdInventoryIndex',
    // 'stdInventoryIndex',
  ].includes(key);
};

/**
 *  ゴミ箱のプロパティのキー
 */
export type TrashBinHeaderKey =
  | 'TrashBinChoice'
  | 'TrashBinExpansion'
  | 'TrashBinWarehouseNumber'
  | 'TrashBinWarehouse'
  | 'TrashBinSupplierNumber'
  | 'TrashBinSupplierName'
  | 'TrashBinBusinessNumber'
  | 'TrashBinBusinessName'
  | 'TrashBinPackage'
  | 'TrashBinUnit'
  | 'TrashBinCustomerNumber'
  | 'TrashBinCustomerName'
  | 'TrashBinDeliveryDestinationName'
  | 'TrashBinDeliveryDestinationNumber'
  | 'TrashBinManufacturer'
  | 'TrashBinEndUser'
  | 'TrashBinDaysRemainUntilDeletion'
  | 'TrashBinArrival'
  | 'TrashBinSalesResults'
  | 'TrashBinLatestInventoryAmount'
  | 'TrashBinExecuteDate'
  | 'TrashBinSupplierForecastResponsibleUser'
  | 'TrashBinCustomerForecastResponsibleUser'
  | 'TrashBinCustomerAnnounce-1'
  | 'TrashBinCustomerAnnounce-2'
  | 'TrashBinCustomerAnnounce-3';

/**
 * テーブルのヘッダーのキーがOrderControlのヘッダーキーかどうか
 * @param key - テーブルのヘッダーのキー
 */
export const isTrashBinHeaderKey = (key: string): key is TrashBinHeaderKey => {
  return [
    'TrashBinChoice',
    'TrashBinExpansion',
    'TrashBinWarehouseNumber',
    'TrashBinWarehouse',
    'TrashBinSupplierNumber',
    'TrashBinSupplierName',
    'TrashBinBusinessNumber',
    'TrashBinBusinessName',
    'TrashBinPackage',
    'TrashBinUnit',
    'TrashBinCustomerNumber',
    'TrashBinCustomerName',
    'TrashBinDeliveryDestinationNumber',
    'TrashBinDeliveryDestinationName',
    'TrashBinManufacturer',
    'TrashBinEndUser',
    'TrashBinDaysRemainUntilDeletion',
    'TrashBinArrival',
    'TrashBinSalesResults',
    'TrashBinLatestInventoryAmount',
    'TrashBinExecuteDate',
    'TrashBinSupplierForecastResponsibleUser',
    'TrashBinCustomerForecastResponsibleUser',
    'TrashBinCustomerAnnounce-1',
    'TrashBinCustomerAnnounce-2',
    'TrashBinCustomerAnnounce-3',
  ].includes(key);
};

/**
 * 取引編集のユニークプロパティのキー
 */
export type EditBusinessHeaderKey = 'editBusinessCustomerName' | 'editBusinessSalesDestinationName' | 'editBusiness';

/**
 * テーブルのヘッダーキーがEditBusinessHeaderKeyかどうか
 * @param key - テーブルのヘッダーキー
 */
export const isEditBusinessHeaderKey = (key: string): key is EditBusinessHeaderKey => {
  return ['editBusinessCustomerName', 'editBusinessSalesDestinationName', 'editBusiness'].includes(key);
};
